import { useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Stack from '@zenbusiness/zui/Stack';
import PropTypes from 'prop-types';

import ActivateCardModal from '#client/components/ActivateCardModal';
import CreateNewCardModal from '#client/components/CreateNewCardModal';
import ResetPinModal from '#client/components/ResetPinModal';
import SetPinModal from '#client/components/SetPinModal';
import TransactionLimitsModal from '#client/components/TransactionLimitsModal';
import { CARD_STATUS } from '#client/constants/cards';
import { MODAL_NAMES } from '#client/constants/modals';
import useCardsContext from '#client/hooks/useCardsContext';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import { getDiffTime } from '#client/utils/date';
import { CARD_PIN_STATUS } from '#graphql/cards/constants';

import SettingOptions from './components/SettingOptions';

import { ENABLE_REQUEST_CARD_STATUS, DISABLE_FREEZE_CARD_STATUS } from './constants/status';
import { HeadingStyled } from './index.styles';

export default function CardsSetting({ handleClose }) {
  const {
    physicalCard,
    virtualCard,
    physicalCardPinStatus,
    notHasPhysicalCard,
    notHasVirtualCard,
    error: cardOrPinError,
    handleCreateCard,
    isCreateCardLoading,
    loading: loadingCards
  } = useCardsContext();

  const diffHours = Math.abs(getDiffTime(physicalCard?.updatedAt, 'h'));
  const isSuspectedFraudCard = physicalCard?.status === CARD_STATUS.SUSPECTED_FRAUD;
  const isShowCancelBySuspectedFraudBanner = isSuspectedFraudCard && diffHours >= 24;

  const { openModal } = useModalContext();

  useModalDisplayTracker({
    elementName: 'debit_card_modal',
    elementText: 'debit_card_modal',
    appPageName: 'active_banking_dashboard'
  });
  const cardOptions = useMemo(() => {
    const virtualCardStatus = virtualCard?.status;
    const physicalCardStatus = physicalCard?.status;
    const physicalCardActivatable = physicalCard?.activatable;
    const isHideActivatePhysicalCard =
      !physicalCardStatus ||
      !physicalCardActivatable ||
      physicalCardStatus !== CARD_STATUS.INACTIVE;

    /**
     * Show the "Request a card" option when any of these are true:
     * 1. The customer has no physical card on file (e.g. it was closed)
     * 2. The physical card is active or frozen
     * 3. The virtual card is active or frozen
     * 4. The physical card was flagged as suspected fraud more than 24h ago (waiting period elapsed)
     */
    const canRequestPhysicalCard =
      notHasPhysicalCard ||
      ENABLE_REQUEST_CARD_STATUS.includes(physicalCardStatus) ||
      ENABLE_REQUEST_CARD_STATUS.includes(virtualCardStatus) ||
      isShowCancelBySuspectedFraudBanner;

    const isDisableFreezeCard =
      (DISABLE_FREEZE_CARD_STATUS.includes(physicalCardStatus) || notHasPhysicalCard) &&
      (DISABLE_FREEZE_CARD_STATUS.includes(virtualCardStatus) || notHasVirtualCard);

    const isDisableManagePIN = cardOrPinError || physicalCardStatus !== CARD_STATUS.ACTIVE;

    const isPhysicalCardFrozen = physicalCardStatus === CARD_STATUS.FROZEN;

    const isDisableGenerateVirtualCard = !!virtualCardStatus || isCreateCardLoading || loadingCards;

    const isDisableAddCardWallet = !virtualCardStatus || isCreateCardLoading || loadingCards;

    return [
      {
        key: 'REQUEST_A_NEW_CARD',
        name: 'Request a card',
        disabled: !canRequestPhysicalCard,
        hidden: false
      },
      {
        key: 'FREEZE_A_CARD',
        name: 'Freeze a card',
        disabled: !!isDisableFreezeCard,
        hidden: false
      },
      {
        key: 'UNFREEZE_A_CARD',
        name: 'Unfreeze a card',
        disabled: !isPhysicalCardFrozen,
        hidden: !isPhysicalCardFrozen
      },
      {
        key: 'MANAGE_PIN',
        name: 'Manage PIN',
        disabled: !!isDisableManagePIN,
        hidden: false
      },
      {
        key: 'ACTIVE_PHYSICAL_CARD',
        name: 'Activate physical card',
        hidden: isHideActivatePhysicalCard,
        disabled: false
      },
      {
        key: 'GENERATE_VIRTUAL_CARD',
        name: 'Generate virtual card',
        disabled: isDisableGenerateVirtualCard,
        hidden: !!virtualCardStatus
      },
      {
        key: 'ADD_TO_WALLET',
        name: 'Add to wallet',
        disabled: isDisableAddCardWallet,
        hidden: false
      },
      {
        key: 'VIEW_ALL_TRANSACTION_LIMITS',
        name: 'View all transaction limits',
        disabled: false,
        hidden: false
      }
    ];
  }, [
    physicalCard?.status,
    virtualCard?.status,
    physicalCard?.activatable,
    cardOrPinError,
    notHasPhysicalCard,
    notHasVirtualCard,
    isCreateCardLoading,
    loadingCards,
    isShowCancelBySuspectedFraudBanner
  ]);

  const trackRequestNewCardPressed = useButtonPressedCallback({
    elementName: 'new_card_request',
    elementText: 'Request a new card',
    appPageName: 'active_banking_dashboard'
  });
  const trackManagePinPressed = useButtonPressedCallback({
    elementName: 'manage_pin_dc',
    elementText: 'Manage PIN',
    appPageName: 'active_banking_dashboard'
  });
  const trackFreezeACardPressed = useButtonPressedCallback({
    elementName: 'freeze_dc',
    elementText: 'Freeze a card',
    appPageName: 'active_banking_dashboard'
  });
  const trackUnfreezeACardPressed = useButtonPressedCallback({
    elementName: 'unfreeze_dc',
    elementText: 'Unfreeze a card',
    appPageName: 'active_banking_dashboard'
  });
  const trackGenerateVirtualCardPressed = useButtonPressedCallback({
    elementName: 'generate_virtual_card',
    elementText: 'Generate virtual card',
    appPageName: 'active_banking_dashboard'
  });

  const handleSelectItem = (key) => {
    switch (key) {
      case 'REQUEST_A_NEW_CARD':
        /**
         * Tracking Details: Tracking added to Request new card item
         * Question: How many users click the Request new card item?
         */
        trackRequestNewCardPressed();

        openModal({
          component: CreateNewCardModal
        });
        break;
      case 'MANAGE_PIN':
        /**
         * Tracking Details: Tracking added to manage pin item
         * Question: How many users click the manage pin item?
         */
        trackManagePinPressed();

        if (cardOrPinError) {
          return;
        }
        if (physicalCardPinStatus === CARD_PIN_STATUS.NOTSET) {
          openModal({
            component: SetPinModal
          });
        }
        if (physicalCardPinStatus === CARD_PIN_STATUS.SET) {
          openModal({
            component: ResetPinModal
          });
        }
        break;
      case 'FREEZE_A_CARD':
        /**
         * Tracking Details: Tracking added to freeze a card
         * Question: How many users click the freeze a card?
         */
        trackFreezeACardPressed();

        openModal({
          name: MODAL_NAMES.FREEZE_CARD_MODAL
        });
        break;
      case 'UNFREEZE_A_CARD':
        /**
         * Tracking Details: Tracking added to unfreeze a card
         * Question: How many users click the unfreeze a card?
         */
        trackUnfreezeACardPressed();

        openModal({
          name: MODAL_NAMES.UNFREEZE_CARD_MODAL,
          cardId: physicalCard?.id,
          last4Digits: physicalCard?.last4Digits
        });
        break;
      case 'ACTIVE_PHYSICAL_CARD':
        openModal({
          component: ActivateCardModal
        });
        break;
      case 'GENERATE_VIRTUAL_CARD':
        /**
         * Tracking Details: Tracking generate virtual card
         * Question: How many users click generate virtual card?
         */
        trackGenerateVirtualCardPressed();
        handleCreateCard();
        break;
      case 'ADD_TO_WALLET':
        openModal({
          name: MODAL_NAMES.ADD_CARD_WALLET_MODAL
        });
        setTimeout(() => {
          /**
           * Should close card modal after open add card wallet
           * Should only show the data returned from VGS once in the DOM
           */
          handleClose();
        }, 1000);
        break;

      case 'VIEW_ALL_TRANSACTION_LIMITS':
        openModal({
          component: TransactionLimitsModal
        });
        break;
      default:
        break;
    }
  };

  return (
    <Stack gap={0}>
      <HeadingStyled
        family="secondary"
        size="2xl"
        weight="bold"
      >
        Debit card settings
      </HeadingStyled>
      <SettingOptions
        handleSelectItem={handleSelectItem}
        options={cardOptions}
      />
    </Stack>
  );
}

CardsSetting.propTypes = {
  handleClose: PropTypes.func
};
